/**
 * The primary navigation, rendered from the capability list the server derived
 * and from nothing else.
 *
 * No role is read here and none is inferred. An entry is shown when
 * `/control/me` returned the capability it declares, which means an entry
 * appears the moment the server grants it and disappears the moment the server
 * revokes it, with no second copy of the rule in this file to drift.
 *
 * `entry.label()` is called during render rather than resolved once. That is
 * the whole reason `NavEntry.label` is a thunk: the shell cannot hold a message
 * key union over strings the control plane, the ops plane and tenant
 * self-serve own, each area resolves its own label
 * through its own catalogue, and calling on every render is what makes a
 * `setLocale()` re-label the navigation instead of leaving it in the locale
 * that happened to be active when the bundle was imported.
 */

import { createElement, type ReactNode } from 'react';

import { ShellNav, type ShellNavGroup, type ShellNavItem } from '../../chrome/shell';
import type { NavEntry } from '../routes/contract';
import { useMe } from '../session/session';
import { gt } from './groups';
import { ts } from './messages';

/** Every `$name` path segment `entry.path` declares. A caller that does not
 *  supply one of these in `params` has nothing to interpolate the link with,
 *  and TanStack Router renders the literal string `undefined` there instead
 *  of refusing -- so this is checked here, once, rather than trusted of every
 *  caller. */
function requiredParams(path: string): string[] {
  return (path.match(/\$[a-zA-Z0-9_]+/g) ?? []).map((segment) => segment.slice(1));
}

/**
 * `entries` filtered to what the caller may actually see: the server's
 * granted capabilities, and every `$name` segment `params` has resolved.
 * Exposed so the shell's topbar can name the active screen from the exact
 * list `ShellNav` renders, rather than a second guess at what is visible.
 */
export function useVisibleNavEntries(
  entries: NavEntry[],
  params?: Record<string, string>,
): NavEntry[] {
  // One `useMe()` for the whole list rather than a `useCan()` per entry: the
  // entry array is composed from whichever feature areas exist in this build,
  // so calling a hook once per element would make the hook count depend on the
  // route bundles present.
  const me = useMe();
  const capabilities = me.data?.capabilities ?? [];

  function resolves(path: string): boolean {
    return requiredParams(path).every((name) => params?.[name] !== undefined);
  }

  // An entry whose path needs a param the caller has not resolved yet is
  // hidden, the same courtesy as a capability the caller does not hold --
  // a link to `/t/undefined/...` is not a link. An entry whose `activeOn`
  // page is the one open links there, to the open id, rather than back to
  // its own picker.
  return entries
    .filter((entry) => capabilities.includes(entry.requires))
    .filter((entry) => resolves(entry.path))
    .map((entry) =>
      entry.activeOn !== undefined && resolves(entry.activeOn)
        ? { ...entry, path: entry.activeOn }
        : entry,
    );
}

export function Nav(props: {
  entries: NavEntry[];
  campaignEntries?: NavEntry[];
  params?: Record<string, string>;
}): ReactNode {
  const orgEntries = useVisibleNavEntries(props.entries, props.params);
  const campaignEntries = useVisibleNavEntries(props.campaignEntries ?? [], props.params);

  const toItem = (entry: NavEntry): ShellNavItem => ({
    path: entry.path,
    params: props.params,
    search: entry.search,
    activeOn: entry.activeOn,
    label: entry.label(),
    icon: entry.icon,
    badge: entry.badge && createElement(entry.badge, { params: props.params }),
  });

  // One list nested into a tree: an entry with no `parent` is top-level, and
  // an entry naming one becomes that entry's child -- dropped, not promoted,
  // when the name resolves to nothing visible, since a capability that hid
  // the parent hid the group it would have appeared under. `parent` only
  // ever names a top-level entry (`contract.ts`'s own doc comment), so a
  // child's own path can never be looked up as somebody else's parent.
  function toTree(entries: NavEntry[]): ShellNavItem[] {
    const roots = entries.filter((entry) => entry.parent === undefined);
    const items = new Map(roots.map((entry) => [entry.path, toItem(entry)]));
    for (const entry of entries) {
      if (entry.parent === undefined) continue;
      const parent = items.get(entry.parent);
      if (parent === undefined) continue;
      (parent.children ??= []).push(toItem(entry));
      // The chevron is the only cue that a heading opens onto a second
      // level; `ShellNav` turns it with the group's open state.
      parent.indicator = { kind: 'chevron-down' };
    }
    return roots.map((entry) => items.get(entry.path)!);
  }

  const groups: ShellNavGroup[] = [
    { label: gt('group.organisation'), items: toTree(orgEntries) },
    { label: gt('group.campaign'), items: toTree(campaignEntries) },
  ];
  return <ShellNav label={ts('nav.label')} groups={groups} />;
}
